import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { apiHooks } from '@/lib/api';
import { useBranch } from './branch-context';
import { createBranchRecovery } from './branch-recovery';

/**
 * RL-1176: wires the branch recovery to the API client. Mounted once, in the
 * signed-in shell. Returns true when the reloaded `me` holds no branch, so the
 * shell shows the no-access state instead of the page.
 */
export function useBranchRecovery(): { noBranchAccess: boolean } {
  const { t } = useTranslation();
  const { reloadMe } = useSession();
  const { selected, select } = useBranch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [noBranchAccess, setNoBranchAccess] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);

  // Latest values for the long-lived recovery below.
  const latest = useRef({ reloadMe, selected, select, toast, t });
  useLayoutEffect(() => {
    latest.current = { reloadMe, selected, select, toast, t };
  });

  // One recovery per mounted shell: it keeps the single-flight and cooldown
  // state for as long as the handler is registered.
  useEffect(() => {
    const recovery = createBranchRecovery({
      reloadMe: () => latest.current.reloadMe(),
      currentSelection: () => latest.current.selected?.id ?? null,
      select: (id) => latest.current.select(id),
      settled: (outcome) => {
        if (outcome.kind === 'no_branches') {
          setNoBranchAccess(true);
          return;
        }
        setNoBranchAccess(false);
        const { switchedTo } = outcome;
        if (switchedTo) {
          const { toast: toastApi, t: tr } = latest.current;
          toastApi.show({
            tone: 'info',
            title: tr('errors.branchSwitchedTitle'),
            body: tr('errors.branchSwitchedBody', {
              branch:
                switchedTo === 'all' ? tr('auth.allBranches') : switchedTo.name,
            }),
          });
        }
        // Refetch in the commit that applies the new branch: BranchProvider
        // points X-Branch-Id at it in a layout effect, before this passive one.
        setRefetchToken((n) => n + 1);
      },
    });
    const onBranchRejected = () => void recovery.handle();
    apiHooks.onBranchRejected = onBranchRejected;
    return () => {
      if (apiHooks.onBranchRejected === onBranchRejected)
        apiHooks.onBranchRejected = undefined;
    };
  }, []);

  useEffect(() => {
    if (refetchToken > 0) void queryClient.invalidateQueries();
  }, [refetchToken, queryClient]);

  return { noBranchAccess };
}
